import { useState } from 'react'
import { useParams } from 'react-router'
import { AlertTriangle, MailCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAcceptInvite, useMe } from '../api/auth'

export function AcceptInvitePage() {
  const { t } = useTranslation('auth')
  const { token } = useParams<{ token: string }>()
  const { data: me, isLoading } = useMe()
  const acceptInvite = useAcceptInvite()
  const [error, setError] = useState<string | null>(null)

  async function onAccept() {
    if (!token) return
    setError(null)
    try {
      await acceptInvite.mutateAsync(token)
      window.location.assign('/')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div className="min-h-screen bg-background text-text-primary">
      <header className="border-b border-border bg-surface/60">
        <div className="mx-auto flex max-w-2xl items-center justify-center px-6 py-5">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-background">
              V
            </span>
            Voysi AI
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-10">
        {isLoading || !me ? (
          <LoadingCard />
        ) : error ? (
          <ErrorCard
            title={t('acceptInvite.errorTitle')}
            detail={t('acceptInvite.errorDetail')}
          />
        ) : (
          <div className="space-y-6 rounded-xl border border-border bg-surface p-6 shadow-lg">
            <div className="flex items-center gap-4 border-b border-border pb-5">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-surface-elevated text-primary">
                <MailCheck className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-lg font-semibold">
                  {t('acceptInvite.title')}
                </h1>
                <p className="text-sm text-text-secondary">
                  {t('acceptInvite.subtitle')}
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-background px-4 py-3 text-sm">
              <div className="text-xs text-text-secondary">
                {t('acceptInvite.signedInAs')}
              </div>
              <div className="mt-0.5 font-mono text-text-primary">
                {me.user.email}
              </div>
            </div>

            <div className="flex justify-end border-t border-border pt-5">
              <button
                type="button"
                onClick={onAccept}
                disabled={acceptInvite.isPending || !token}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-background hover:bg-primary-hover disabled:opacity-50"
              >
                {acceptInvite.isPending
                  ? t('acceptInvite.accepting')
                  : t('acceptInvite.accept')}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

function LoadingCard() {
  return (
    <div className="animate-pulse space-y-4 rounded-xl border border-border bg-surface p-6">
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-xl bg-surface-elevated" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-1/2 rounded bg-surface-elevated" />
          <div className="h-3 w-1/3 rounded bg-surface-elevated" />
        </div>
      </div>
      <div className="h-12 rounded bg-surface-elevated" />
      <div className="h-10 w-32 self-end rounded bg-surface-elevated" />
    </div>
  )
}

function ErrorCard({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-error/50 bg-error/10 p-5">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-error" />
      <div>
        <div className="font-medium text-error">{title}</div>
        <p className="mt-1 text-sm text-text-secondary">{detail}</p>
      </div>
    </div>
  )
}
